import React from 'react';
import { Box, Container, Divider, Paper, Stack, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import BrandLockup from '../components/brand/BrandLockup';
import BrandMark from '../components/brand/BrandMark';
import PublicHeader from '../components/public/PublicHeader';
import PublicFooter from '../components/public/PublicFooter';
import './SupportPages.css';

const brandColors = [
  { key: 'brand.pine', value: '#245444', color: '#245444' },
  { key: 'brand.brass', value: '#D8AD66', color: '#D8AD66' },
  { key: 'brand.paper', value: '#F6F4EE', color: '#F6F4EE', border: true },
];

const Brand: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();

  return (
    <>
      <Helmet>
        <title>{t('brand.pageTitle')}</title>
        <meta name="description" content={t('brand.description')} />
      </Helmet>
      {!user && <PublicHeader />}
      <Box component={user ? 'div' : 'main'} sx={{ minHeight: user ? 'auto' : '100vh', py: { xs: 5, md: 9 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 760, mb: { xs: 4, md: 6 } }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '.16em' }}>
              {t('brand.eyebrow')}
            </Typography>
            <Typography component="h1" variant="h2" sx={{ mt: 1, fontWeight: 700, letterSpacing: '-.045em', fontSize: { xs: '2.25rem', md: '3.25rem' } }}>
              {t('brand.headline')}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1.5, fontSize: { xs: 16, md: 18 }, lineHeight: 1.7 }}>
              {t('brand.description')}
            </Typography>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              position: 'relative', overflow: 'hidden', p: { xs: 3, sm: 5, md: 8 },
              borderRadius: 4,
              background: theme => `radial-gradient(ellipse at 94% 4%, ${theme.palette.action.selected} 0, transparent 42%), ${theme.palette.background.paper}`,
            }}
          >
            <Box sx={{ position: 'absolute', width: 260, height: 260, border: '1px solid', borderColor: 'divider', borderRadius: '50%', right: -136, top: -150, opacity: .75 }} />
            <Stack alignItems="center" justifyContent="center" spacing={{ xs: 3, md: 4 }} sx={{ position: 'relative', minHeight: { xs: 210, md: 280 } }}>
              <BrandLockup markSize="clamp(56px, 12vw, 148px)" fontSize="clamp(2rem, 8vw, 6.1rem)" gap={1.5} />
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.75, py: .7, borderRadius: 99, bgcolor: 'rgba(216,173,102,.16)' }}>
                <Typography component="span" aria-hidden="true" sx={{ color: '#B47826', fontSize: 20, fontWeight: 800, lineHeight: 1 }}>e</Typography>
                <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 800, letterSpacing: '.08em' }}>{t('brand.electronicBadge')}</Typography>
              </Stack>
              <Divider sx={{ width: { xs: '74%', md: '52%' } }} />
              <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 580, lineHeight: 1.7 }}>
                {t('brand.lockupDescription')}
              </Typography>
            </Stack>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.15fr .85fr' }, gap: 2, mt: 2 }}>
            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3 }}>
              <Stack direction="row" spacing={2.5} alignItems="center">
                <Box sx={{ width: 104, height: 104, display: 'grid', placeItems: 'center', borderRadius: 3, bgcolor: '#214B3D', flexShrink: 0 }}>
                  <BrandMark size={78} onDarkSurface />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{t('brand.symbolTitle')}</Typography>
                  <Typography color="text.secondary" sx={{ mt: .75, lineHeight: 1.65 }}>{t('brand.symbolDescription')}</Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{t('brand.paletteTitle')}</Typography>
              <Stack spacing={1.5}>
                {brandColors.map(color => (
                  <Stack key={color.key} direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: color.color, border: color.border ? '1px solid #D8DED8' : 'none', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{t(color.key)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{color.value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Box>
        </Container>
      </Box>
      <PublicFooter />
    </>
  );
};

export default Brand;
